import { UserClaims } from 'app/@types/dto/user-claims.model';
import { User } from 'app/auth/models';
import { BaseStateInterface } from './basestate.state';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { AuthenticationService } from 'app/auth/service';
import { HttpClient, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { UrlService } from '../services/datasources/url.service';
import { LocalStorageKeys, StorageService } from '../services/storage/storage.service';
import { BehaviorSubject, Observable, Subject, catchError, first, map, mergeMap, throwError } from 'rxjs';
import { OrganizationDto } from 'app/@types/dto/organization-dto.model';
import { UserHttpService } from '@services/datasources/user-http.service';
import { FormulaireCallCompletion } from '../apps/components/settings-form/settings-form.component';
import { QuestionnaireAction } from './questionnaire.state';
import { AppRoutingService } from '@services/app-routing/app-routing.service';
import { ConfigService } from 'app/app-config.service';
import { QuestionnaireHttpService } from '@services/datasources/questionnaire-http.service';

export enum UserStatus {
    NONE = 'none',
    TERMS = 'terms',
    ONBOARDED = 'onboarded',
}

export interface CurrentUserStateModel extends BaseStateInterface {
    currentUser: User | undefined;
    currentUserClaims: UserClaims | undefined;
    apiCallResult: HttpStatusCode | undefined;
    userStatus: UserStatus;
}

export namespace UserAction {
    export class Activate {
        public static type = '[CurrentUser UI] Activate CurrentUser';
        constructor() {}
    }

    export class Update {
        public static type = '[CurrentUser UI] Update CurrentUser';
        constructor(public orgId?: string) {}
    }

    export class UpdateWithValue {
        public static type = '[CurrentUser UI] UpdateWithValue CurrentUser';
        constructor(public claims: UserClaims) {}
    }

    export class Deactivate {
        public static type = '[CurrentUser UI] Deactivate CurrentUser';
        constructor() {}
    }

    export class UpdateOrganization {
        public static type = '[CurrentUser UI] Update Organization';
        constructor(
            public org: OrganizationDto,
            public isOnboarding: boolean,
            public callResultSignal?: Subject<FormulaireCallCompletion>,
        ) {}
    }

    export class UpdateUserStatus {
        public static type = '[CurrentUser UI] Update CurrentUserStatus';
        constructor(public status: UserStatus) {}
    }
}

@State<CurrentUserStateModel>({
    name: 'user',
    defaults: {
        currentUser: undefined,
        currentUserClaims: undefined,
        isInit: false,
        isLoading: false,
        apiCallResult: undefined,
        userStatus: UserStatus.NONE,
        errors: [],
    },
})
@Injectable()
export class UserState {
    constructor(
        private authenticationService: AuthenticationService,
        private http: HttpClient,
        private urlService: UrlService,
        private _authenticationService: AuthenticationService,
        private localStorageService: StorageService,
        private userService: UserHttpService,
        private store: Store,
        private appRouting: AppRoutingService,
    ) {}

    //----- store action -----//
    @Action(UserAction.Activate)
    activate(ctx: StateContext<CurrentUserStateModel>, action: UserAction.Activate) {
        ctx.patchState({
            isLoading: true,
        });

        if (ctx.getState().isInit) {
            ctx.patchState({
                isLoading: false,
            });
            return;
        }

        this.authenticationService.currentUser$.subscribe((user) => {
            if (user) {
                ctx.patchState({
                    currentUser: user,
                    userStatus:
                        ctx.getState().userStatus === UserStatus.NONE && user?.has_accepted_terms === '1'
                            ? UserStatus.TERMS
                            : ctx.getState().userStatus,
                });
            } else {
                let errors = ctx.getState().errors;
                errors.push('Could not get Session User');
                ctx.patchState({
                    currentUser: undefined,
                    errors: errors,
                });
                return;
            }
        });

        let orgId: string | undefined = this.localStorageService.getGlobalItem(LocalStorageKeys.sudoMod);

        this.fetchUserClaim(ctx, orgId);

        this._authenticationService.currentUser$.subscribe((x) => {
            if (x == null) {
                this.disableUserClaim(ctx, []);
            }
        });
    }

    @Action(UserAction.UpdateOrganization)
    updateOrganizationClaims(ctx: StateContext<CurrentUserStateModel>, action: UserAction.UpdateOrganization) {
        this.userService.updateOrganization(action.org.uid!, action.org, action.isOnboarding).subscribe({
            next: (org) => {
                if (org) {
                    let claims = { ...ctx.getState().currentUserClaims! };
                    claims.organization = org;
                    this.populateUserClaims(claims, 200, ctx);
                    let onboardingAwaiter = new BehaviorSubject<boolean | undefined>(undefined);
                    if (action.isOnboarding) {
                        onboardingAwaiter.subscribe({
                            next: (success) => {
                                if (success === undefined) return;
                                if (success) {
                                    if (action.callResultSignal) {
                                        action.callResultSignal.next('orgSuccess');
                                        action.callResultSignal.complete();
                                    }
                                } else {
                                    if (action.callResultSignal) {
                                        action.callResultSignal.next('orgFail');
                                        action.callResultSignal.complete();
                                    }
                                }
                                onboardingAwaiter.complete();
                            },
                        });
                    } else {
                        if (action.callResultSignal) {
                            action.callResultSignal.next('orgSuccess');
                            action.callResultSignal.complete();
                        }
                    }
                }
            },
            error: (e) => {
                if (action.callResultSignal) {
                    action.callResultSignal.next('orgFail');
                    action.callResultSignal.complete();
                }
                //todo handle errors if not in the form component
            },
        });
    }

    @Action(UserAction.Update)
    updateUserClaim(ctx: StateContext<CurrentUserStateModel>, action: UserAction.Update) {
        if (action.orgId) this.localStorageService.setGlobalItem(LocalStorageKeys.sudoMod, action.orgId);
        else this.fetchUserClaim(ctx, action.orgId);
    }

    @Action(UserAction.Deactivate)
    deactivateUserClaim(ctx: StateContext<CurrentUserStateModel>, action: UserAction.Deactivate) {
        this.disableUserClaim(ctx, []);
    }

    @Action(UserAction.UpdateWithValue)
    updateUserClaimWithValue(ctx: StateContext<CurrentUserStateModel>, action: UserAction.UpdateWithValue) {
        this.populateUserClaims(action.claims, 200, ctx);
    }

    @Action(UserAction.UpdateUserStatus)
    updateUserStatus(ctx: StateContext<CurrentUserStateModel>, action: UserAction.UpdateUserStatus) {
        ctx.patchState({
            userStatus: action.status,
        });
    }

    //-------------- internal functions ----------------//

    private populateUserClaims(
        claims: UserClaims,
        requestStatus: HttpStatusCode,
        ctx: StateContext<CurrentUserStateModel>,
    ) {
        if (!claims) this.appRouting.navigateToLoginPage();

        ctx.patchState({
            isInit: true,
            isLoading: false,
            errors: [],
            currentUserClaims: claims,
            userStatus:
                ctx.getState().userStatus === UserStatus.TERMS && claims?.organization?.isOnboarded
                    ? UserStatus.ONBOARDED
                    : ctx.getState().userStatus,
            apiCallResult: requestStatus,
        });
    }

    private disableUserClaim(ctx: StateContext<CurrentUserStateModel>, errors: string[]) {
        this.localStorageService.removeItem(LocalStorageKeys.sudoMod);
        ctx.patchState({
            isInit: false,
            isLoading: false,
            errors: errors,
            currentUserClaims: undefined,
        });
    }

    public fetchUserClaim(ctx: StateContext<CurrentUserStateModel>, organization?: string): void {
        this.get(organization).subscribe({
            next: (response) => {
                this.populateUserClaims(response.body as UserClaims, response.status, ctx);
            },
            error: (err) => {
                this.authenticationService.logout(false).pipe(first()).subscribe();
                this.disableUserClaim(ctx, ['Could not get the userClaim']);
            },
        });
    }

    private get(organization?: string): Observable<HttpResponse<UserClaims>> {
        return this.http.get<UserClaims>(this.urlService.getUserClaimsUrl(organization), { observe: 'response' }).pipe(
            catchError((err) => {
                throw 'GetUserClaimsFailed';
            }),
        );
    }

    //-------------- selectors ----------------//
    @Selector([UserState])
    static getCurrentUser(state: CurrentUserStateModel) {
        return state.currentUser;
    }

    @Selector([UserState])
    static getCurrentUserClaims(state: CurrentUserStateModel) {
        return state.currentUserClaims;
    }

    @Selector([UserState])
    static getResponseStatus(state: CurrentUserStateModel) {
        return state.apiCallResult;
    }

    @Selector([UserState])
    static getUserStatus(state: CurrentUserStateModel) {
        return state.userStatus;
    }
}
